/**
 * PartnersView - Full-page view for partners management
 */

import React, { useState, useCallback, useEffect, useMemo } from "react";
import { useNavigate } from "react-router-dom";
import {
  Box,
  Button,
  Alert,
  Snackbar,
  CircularProgress,
  TextField,
  InputAdornment,
} from "@mui/material";
import { Add, Refresh, Search } from "@mui/icons-material";
import { Tag, Space, Table, Typography } from "antd";
import { EditOutlined, EyeOutlined } from "@ant-design/icons";
import { getPartners } from "../services/partnerService";
import { getDrivers } from "../../drivers/services/driverService";
import { getVehicles } from "../../vehicles/services/vehicleService";
import PartnerForm from "../components/PartnerForm";
import ProviderApplicationsQueue from "../components/ProviderApplicationsQueue";
import {
  PageHeader,
  PageFilters,
} from "../../../shared/components/pageShell";

const { Text } = Typography;

const matchesPartnerSearch = (partner, searchQuery) => {
  if (!searchQuery) return true;
  const searchLower = searchQuery.toLowerCase();
  return (
    partner.name?.toLowerCase().includes(searchLower) ||
    partner.contactPerson?.toLowerCase().includes(searchLower) ||
    partner.phone?.includes(searchQuery)
  );
};

/**
 * PartnersView Component
 */
const PartnersView = () => {
  const navigate = useNavigate();

  const [partners, setPartners] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [editPartner, setEditPartner] = useState(null);
  const [formOpen, setFormOpen] = useState(false);
  const [searchQuery, setSearchQuery] = useState("");
  const [notification, setNotification] = useState({
    open: false,
    message: "",
    severity: "success",
  });

  const fetchPartners = useCallback(async () => {
    setLoading(true);
    setError(null);
    try {
      const result = await getPartners({ isActive: true });
      if (!result.success) {
        setError(result.error || "Failed to fetch partners");
        setPartners([]);
        return;
      }

      const partnerList = result.data || [];
      setPartners(
        partnerList.map((partner) => ({
          ...partner,
          driverCount: 0,
          vehicleCount: 0,
        }))
      );
      setLoading(false);

      const partnersWithStats = await Promise.all(
        partnerList.map(async (partner) => {
          try {
            const [driversResult, vehiclesResult] = await Promise.all([
              getDrivers({ partnerId: partner.id }),
              getVehicles({ partnerId: partner.id }),
            ]);

            return {
              ...partner,
              driverCount: driversResult.success
                ? (driversResult.data || []).length
                : 0,
              vehicleCount: vehiclesResult.success
                ? (vehiclesResult.data || []).length
                : 0,
            };
          } catch (err) {
            console.error(`Error fetching stats for partner ${partner.id}:`, err);
            return {
              ...partner,
              driverCount: 0,
              vehicleCount: 0,
            };
          }
        })
      );

      setPartners(partnersWithStats);
    } catch (err) {
      setError(err.message || "Failed to fetch partners");
      setPartners([]);
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    fetchPartners();
  }, [fetchPartners]);

  const displayedPartners = useMemo(
    () => partners.filter((partner) => matchesPartnerSearch(partner, searchQuery)),
    [partners, searchQuery]
  );

  const handleCreatePartner = useCallback(() => {
    setEditPartner(null);
    setFormOpen(true);
  }, []);

  const handleEditPartner = useCallback((partner) => {
    setEditPartner(partner);
    setFormOpen(true);
  }, []);

  const handleCloseForm = useCallback(() => {
    setFormOpen(false);
    setEditPartner(null);
  }, []);

  const handlePartnerSaved = useCallback(() => {
    setFormOpen(false);
    setEditPartner(null);
    fetchPartners();
    setNotification({
      open: true,
      message: editPartner
        ? "Partner updated successfully"
        : "Partner created successfully",
      severity: "success",
    });
  }, [editPartner, fetchPartners]);

  const handleCloseNotification = useCallback(() => {
    setNotification((prev) => ({ ...prev, open: false }));
  }, []);

  const handleViewPartner = useCallback(
    (partner) => {
      navigate(`/partners/${partner.id}`);
    },
    [navigate]
  );

  const columns = [
    {
      title: "Name",
      dataIndex: "name",
      key: "name",
      render: (name) => <Text strong>{name || "N/A"}</Text>,
    },
    {
      title: "Contact Person",
      dataIndex: "contactPerson",
      key: "contactPerson",
      render: (person) => <Text>{person || "N/A"}</Text>,
    },
    {
      title: "Phone",
      dataIndex: "phone",
      key: "phone",
      render: (phone, record) => (
        <Space direction="vertical" size={0}>
          <Text type="secondary">{phone || "N/A"}</Text>
          {record.phoneIsWhatsApp && <Tag color="green">WhatsApp</Tag>}
        </Space>
      ),
    },
    {
      title: "Email",
      dataIndex: "email",
      key: "email",
      render: (email) => <Text type="secondary">{email || "N/A"}</Text>,
    },
    {
      title: "Drivers",
      dataIndex: "driverCount",
      key: "driverCount",
      render: (count) => <Tag color="blue">{count || 0}</Tag>,
    },
    {
      title: "Vehicles",
      dataIndex: "vehicleCount",
      key: "vehicleCount",
      render: (count) => <Tag color="green">{count || 0}</Tag>,
    },
    {
      title: "Status",
      dataIndex: "isActive",
      key: "isActive",
      render: (isActive) => (
        <Tag color={isActive ? "green" : "red"}>
          {isActive ? "Active" : "Inactive"}
        </Tag>
      ),
    },
    {
      title: "Actions",
      key: "actions",
      render: (_, record) => (
        <Space>
          <Button
            type="link"
            icon={<EyeOutlined />}
            onClick={() => handleViewPartner(record)}
          >
            View
          </Button>
          <Button
            type="link"
            icon={<EditOutlined />}
            onClick={() => handleEditPartner(record)}
          >
            Edit
          </Button>
        </Space>
      ),
    },
  ];

  if (error) {
    return (
      <Box p={3}>
        <Alert severity="error">Error: {error}</Alert>
      </Box>
    );
  }

  return (
    <>
      <ProviderApplicationsQueue />
      <PageHeader
        title="Partners"
        actions={
          <Button
            variant="contained"
            startIcon={<Add />}
            onClick={handleCreatePartner}
            sx={{
              width: { xs: "100%", sm: "auto" },
              flex: { xs: 1, md: "none" },
              textTransform: "none",
              fontWeight: 600,
              minHeight: { xs: 44, md: 36 },
            }}
          >
            Add Partner
          </Button>
        }
      />

      <PageFilters>
        <TextField
          placeholder="Search partners..."
          value={searchQuery}
          onChange={(e) => setSearchQuery(e.target.value)}
          size="small"
          fullWidth
          InputProps={{
            startAdornment: (
              <InputAdornment position="start">
                <Search />
              </InputAdornment>
            ),
          }}
          sx={{
            minWidth: { xs: "100%", sm: 250 },
            flex: { sm: 1 },
            bgcolor: "background.paper",
          }}
        />
        <Button
          variant="outlined"
          startIcon={<Refresh />}
          onClick={fetchPartners}
          sx={{ width: { xs: "100%", sm: "auto" } }}
        >
          Refresh
        </Button>
      </PageFilters>

      {loading ? (
        <Box display="flex" justifyContent="center" p={4}>
          <CircularProgress />
        </Box>
      ) : (
        <Box sx={{ overflowX: "auto" }}>
          <Table
            columns={columns}
            dataSource={displayedPartners}
            rowKey="id"
            size="small"
            scroll={{ x: "max-content" }}
            pagination={{
              pageSize: 20,
              showSizeChanger: true,
              showTotal: (total) => `Total ${total} partners`,
            }}
          />
        </Box>
      )}

      <PartnerForm
        open={formOpen}
        partner={editPartner}
        onClose={handleCloseForm}
        onSave={handlePartnerSaved}
      />

      <Snackbar
        open={notification.open}
        autoHideDuration={6000}
        onClose={handleCloseNotification}
        anchorOrigin={{ vertical: "bottom", horizontal: "right" }}
      >
        <Alert
          onClose={handleCloseNotification}
          severity={notification.severity}
          sx={{ width: "100%" }}
        >
          {notification.message}
        </Alert>
      </Snackbar>
    </>
  );
};

export default PartnersView;
